'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Locale } from '@/lib/i18n/config';

type LanguageContextType = {
  language: string;
  changeLanguage: (lang: string) => void;
  isRTL: boolean;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const router = useRouter();
  const currentLocale = (params?.locale as Locale) || 'en';
  const [language, setLanguage] = useState(currentLocale);
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    setLanguage(currentLocale);

    // Update RTL status
    const rtlLanguages = ['ar', 'fa'];
    const newIsRTL = rtlLanguages.includes(currentLocale);
    setIsRTL(newIsRTL);

    // Update document direction
    document.documentElement.dir = newIsRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLocale;
  }, [currentLocale]);

  const changeLanguage = (lang: string) => {
    // Navigate to new locale
    const pathname = window.location.pathname;
    let newPathname = pathname;

    // Remove current locale from pathname if present
    if (pathname.startsWith(`/${currentLocale}`)) {
      newPathname = pathname.slice(currentLocale.length + 1) || '/';
    }

    router.push(`/${lang}${newPathname}`);
    localStorage.setItem('preferred-locale', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
